// ============================================
// COMPONENT: FaultsSection (para PlantDetailView)
// ============================================
// Archivo: src/components/plant/FaultsSection.jsx

import React from 'react';
import { AlertTriangle, Clock, AlertCircle } from 'lucide-react';
import { formatTipoFalla, formatEstadoFalla, formatPrioridad } from '../../utils/faultFormatters';

const FaultsSection = ({ faults = [], onUpdateFault, canUpdate = true }) => {
  if (!faults || faults.length === 0) {
    return null;
  }

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'urgente': return 'bg-red-600';
      case 'alta': return 'bg-orange-600';
      case 'media': return 'bg-yellow-600';
      case 'baja': return 'bg-blue-600';
      default: return 'bg-gray-600';
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'reportada': return 'bg-red-500';
      case 'en_revision': return 'bg-yellow-500';
      case 'esperando_repuesto': return 'bg-orange-500';
      case 'resuelta': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="mb-6">
      <div className="bg-red-900/20 border-2 border-red-500 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="text-red-500 animate-pulse" size={28} />
            <h2 className="text-xl font-bold text-white">
              Fallas Activas ({faults.length})
            </h2>
          </div>
        </div>

        <div className="space-y-3">
          {faults.map((fault) => (
            <div
              key={fault.id}
              className="bg-slate-800 rounded-lg p-4 border border-slate-700 hover:border-red-500 transition-colors overflow-hidden"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded text-xs font-bold text-white ${getPriorityColor(fault.prioridad)}`}>
                      {formatPrioridad(fault.prioridad).toUpperCase()}
                    </span>
                    <span className={`px-3 py-1 rounded text-xs font-semibold text-white ${getEstadoColor(fault.estado)}`}>
                      {formatEstadoFalla(fault.estado)}
                    </span>
                    {fault.equipment_name && (
                      <span className="text-sm text-slate-400">
                        {fault.equipment_name}
                      </span>
                    )}
                  </div>

                  <p className="text-white font-semibold mb-1">
                    {formatTipoFalla(fault.tipo_falla)}
                  </p>

                  <p className="text-slate-300 text-sm mb-2">
                    {fault.descripcion}
                  </p>

                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span>
                        Reportado: {fault.responsable_reporte || 'No especificado'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <AlertCircle size={14} />
                      <span>
                        {new Date(fault.fecha_reporte).toLocaleDateString('es-CL')} {' '}
                        {fault.hora_inicio ? new Date(fault.hora_inicio).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  </div>

                  {fault.accion_inmediata && (
                    <div className="mt-2 p-2 bg-slate-700 rounded text-sm text-slate-300">
                      <span className="font-semibold">Acción tomada:</span> {fault.accion_inmediata}
                    </div>
                  )}
                </div>

                <div className="ml-3 shrink-0">
                  {canUpdate && (
                    <button
                      onClick={() => onUpdateFault(fault)}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Actualizar
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FaultsSection;
